import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageSquare, XCircle, Phone, Calendar, 
  UserX, RefreshCw, CheckCircle2, UserCheck, 
  ArrowRight, Brain, AlertCircle, Clock, Play
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogTrigger } from '../ui/dialog';

const LenaWorkflowSlide = () => {
  const [step, setStep] = useState(0);

  // Animation Sequence
  useEffect(() => {
    const interval = setInterval(() => {
      setStep((prev) => {
        if (prev >= 8) return 0; // Restart loop
        return prev + 1;
      });
    }, 3500); // 3.5s per step
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="h-full w-full bg-slate-50 flex items-center justify-center p-12 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
             <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-100/40 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2" />
             <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-100/40 rounded-full blur-[80px] translate-y-1/2 -translate-x-1/2" />
        </div>

        {/* Floating Agent Header (Top Left) */}
        <div className="absolute top-8 left-8 xl:left-12 z-50 flex items-center gap-4 bg-white/40 backdrop-blur-md p-2 pr-6 rounded-full border border-white/20 shadow-lg">
            <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-white shadow-sm">
                 <img src="/assets/leena.png" alt="Leena" className="w-full h-full object-cover" />
            </div>
            <div className="text-left">
                <h3 className="text-lg font-bold text-slate-900 leading-none">Leena</h3>
                <p className="text-xs text-blue-600 font-bold uppercase tracking-wide">Schedule Optimizer</p>
            </div>
        </div>

        <div className="flex flex-col xl:flex-row h-full w-full max-w-[1600px] gap-8 relative z-10 pt-20 xl:pt-0">
            {/* Left Side: Context */}
            <div className="w-full xl:w-1/3 flex flex-col justify-center space-y-8">
                
                <div className="space-y-4">
                    <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">
                        Intelligent multi-step AI voice calls <br/>
                        <span className="text-blue-600">far outperform standard SMS reminders.</span>
                    </h2>
                    
                    <p className="text-lg text-slate-600 leading-relaxed">
                        Most confirmation tools just send a text and hope for the best. If the patient ignores it, you get a no-show.
                        <br/><br/>
                        Leena executes a <strong>multi-channel, 12-step workflow</strong> to catch cancellations early, reschedule them to save revenue, and instantly fill the gap with a high-value patient.
                    </p>
                </div>

                        {/* Demo Button Overlay */}
                        <div className="absolute bottom-6 right-6 z-50 flex items-center gap-4">
                            {/* Audio Wave Animation */}
                            <div className="flex items-center gap-1 h-8">
                                {[1, 2, 3, 4, 5].map((i) => (
                                    <motion.div
                                        key={i}
                                        animate={{
                                            height: [8, 24, 8],
                                            opacity: [0.5, 1, 0.5]
                                        }}
                                        transition={{
                                            duration: 1,
                                            repeat: Infinity,
                                            delay: i * 0.1,
                                            ease: "easeInOut"
                                        }}
                                        className="w-1 bg-blue-500 rounded-full"
                                    />
                                ))}
                            </div>

                            <Dialog>
                                <DialogTrigger asChild>
                                    <motion.button
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        className="group relative inline-flex items-center gap-3 bg-slate-900/90 backdrop-blur-md px-5 py-3 rounded-full shadow-2xl border border-slate-700/50 hover:border-blue-500/50 transition-all duration-300"
                                    >
                                        <div className="relative flex items-center justify-center w-8 h-8 bg-blue-600 rounded-full text-white shadow-lg group-hover:scale-110 transition-transform duration-300">
                                            <Play className="w-3.5 h-3.5 ml-0.5 fill-current" />
                                            <div className="absolute inset-0 rounded-full bg-blue-400 animate-ping opacity-20" />
                                        </div>
                                        <div className="text-left pr-1">
                                            <div className="text-xs font-bold text-white">Watch Live Demo</div>
                                            <div className="text-[10px] text-slate-300">2 min video</div>
                                        </div>
                                    </motion.button>
                                </DialogTrigger>
                                <DialogContent className="max-w-4xl p-0 overflow-hidden bg-black border-none">
                                    <div className="relative w-full pt-[62.5%]">
                                        <iframe 
                                            src="https://www.loom.com/embed/e3e604e616f64c4289554732b270395c" 
                                            frameBorder="0" 
                                            webkitallowfullscreen="true" 
                                            mozallowfullscreen="true" 
                                            allowFullScreen 
                                            className="absolute top-0 left-0 w-full h-full"
                                        ></iframe>
                                    </div>
                                </DialogContent>
                            </Dialog>
                        </div>

                {/* Removed standard failure card as requested */}
            </div>

            {/* Right Side: Animated Workflow */}
            <div className="w-full xl:w-2/3 h-full flex items-center justify-center">
                <div className="w-full max-w-2xl relative">
                    {/* Timeline Line */}
                    <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-slate-200 z-0" />

                    <div className="space-y-6 relative z-10">
                        
                        {/* Step 1: Initial SMS (Ignored) */}
                        <WorkflowStep 
                            active={step >= 0}
                            icon={MessageSquare}
                            color="blue"
                            title="Step 1: SMS Sent"
                            desc="Patient does not respond."
                            status="Ignored"
                            statusColor="text-slate-400"
                        />

                        {/* Step 2: First Call (No Answer) */}
                        <WorkflowStep 
                            active={step >= 1}
                            icon={Phone}
                            color="indigo"
                            title="Step 2: Follow-up Call"
                            desc="Leena calls to confirm. No answer."
                            status="No Answer"
                            statusColor="text-slate-400"
                        />

                        {/* Step 3: Second Call (Connected) */}
                        <WorkflowStep 
                            active={step >= 2}
                            icon={Phone}
                            color="indigo"
                            title="Step 3: Persistence Call"
                            desc="Leena calls again. Patient answers."
                            status="Connected"
                            statusColor="text-green-600"
                        />

                        {/* Step 4: Intelligent Discovery */}
                        <WorkflowStep 
                            active={step >= 3}
                            icon={Brain}
                            color="purple"
                            title="Step 4: Intent Discovery"
                            desc="Patient reveals they can't make it."
                            status="Cancellation Caught"
                            statusColor="text-amber-500"
                            highlight={step === 3}
                        />

                        {/* Step 5: Save the Patient */}
                        <WorkflowStep 
                            active={step >= 4}
                            icon={RefreshCw}
                            color="green"
                            title="Step 5: Immediate Reschedule"
                            desc="Leena doesn't let them hang up. Books for next week."
                            status="Revenue Retained"
                            statusColor="text-green-600"
                            highlight={step === 4}
                        />

                        {/* Divider - Slot is now empty */}
                        <AnimatePresence>
                            {step >= 5 && (
                                <motion.div 
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    className="pl-16 pr-4 py-2"
                                >
                                    <div className="bg-red-50 border border-red-100 rounded-lg p-3 flex items-center justify-between text-sm text-red-700">
                                        <div className="flex items-center gap-2">
                                            <AlertCircle className="w-4 h-4" />
                                            <span className="font-bold">Slot is now Empty!</span>
                                        </div>
                                        <span className="text-xs">Leena switches to "Fill Mode"</span>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Step 6: Rules Engine */}
                        <WorkflowStep 
                            active={step >= 6}
                            icon={Calendar}
                            color="blue"
                            title="Step 6: Smart Match"
                            desc="Scans waitlist & checks eligibility rules."
                            status="Match Found"
                            statusColor="text-blue-600"
                        />

                        {/* Step 7: Fill Execution */}
                        <WorkflowStep 
                            active={step >= 7}
                            icon={CheckCircle2}
                            color="emerald"
                            title="Step 7: Slot Filled"
                            desc="Calls match, confirms interest, books slot."
                            status="Production Maximized"
                            statusColor="text-emerald-600"
                            highlight={step >= 7}
                        />

                    </div>
                </div>
            </div>
        </div>
    </div>
  );
};

const WorkflowStep = ({ active, icon: Icon, color, title, desc, status, statusColor, highlight }) => {
    const colorClasses = {
        blue: 'bg-blue-100 text-blue-600 border-blue-200',
        indigo: 'bg-indigo-100 text-indigo-600 border-indigo-200',
        purple: 'bg-purple-100 text-purple-600 border-purple-200',
        green: 'bg-green-100 text-green-600 border-green-200',
        emerald: 'bg-emerald-100 text-emerald-600 border-emerald-200',
    };

    return (
        <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: active ? 1 : 0.3, x: active ? 0 : 0 }}
            transition={{ duration: 0.5 }}
            className={`relative pl-16 pr-4 ${highlight ? 'scale-105 transition-transform duration-500' : ''}`}
        >
            <div className={`absolute left-4 top-2 w-8 h-8 rounded-full border-4 border-slate-50 flex items-center justify-center z-10 shadow-sm ${active ? colorClasses[color] : 'bg-slate-200 text-slate-400'}`}>
                <Icon className="w-4 h-4" />
            </div>
            
            <div className={`bg-white p-4 rounded-xl border ${highlight ? 'border-blue-300 shadow-md ring-1 ring-blue-100' : 'border-slate-200 shadow-sm'}`}>
                <div className="flex justify-between items-start mb-1">
                    <span className="font-bold text-slate-900 text-sm">{title}</span>
                    <span className={`text-[10px] font-bold uppercase tracking-wider ${statusColor}`}>{status}</span>
                </div>
                <p className="text-xs text-slate-600 leading-relaxed">
                    {desc}
                </p>
            </div>
        </motion.div>
    );
};

export default LenaWorkflowSlide;























